<template>
  <div
    class="bg-surface-container-low group relative cursor-pointer overflow-hidden"
    :class="[expanded && 'border-outline-variant my-0.5 border shadow']"
  >
    <div class="p-4">
      <h4 class="text-body-medium text-on-surface-variant">
        {{ content }}
      </h4>

      <div v-if="!expanded" class="flex flex-col pt-4">
        <PerspectiveValueBarNew
          :values="perspectiveBarEntries"
          :all-perspectives="topicPerspectives"
          :scale="scale.items.map((item) => ({ value: item.value, label: item.label! }))"
          :animation-delay="index * 70"
        />
      </div>

      <div v-else class="relative flex w-full flex-row gap-1 overflow-hidden pt-2">
        <DistributeItems
          class="absolute! top-4 right-0 left-0 h-24"
          direction="horizontal"
          collapse-overflow
          :margin-percentage="100 / scale.items.length / 2"
          :points="distributedItems"
          :size="20"
          v-slot="{ item, count }"
        >
          <div class="relative h-full w-full">
            <BaseMemberAvatarShaped
              v-if="item.member && count == 1"
              size="20"
              :member="item.member"
              color=""
              :all-perspectives="topicPerspectives"
              :perspective="item.perspectiveKey"
            />
            <PerspectiveShape
              v-else
              class="p-px"
              :perspective="item.perspectiveKey"
              :all-perspectives="topicPerspectives"
              :hide-border="item.member !== null && count == 1"
            >
              <div
                v-if="count > 1"
                class="text-label-small border-tertiary bg-tertiary-container text-on-tertiary-container absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full border"
              >
                <span>
                  {{ count }}
                </span>
              </div>
            </PerspectiveShape>
          </div>
        </DistributeItems>
        <div class="flex w-full flex-row gap-0.5">
          <div
            v-for="scaleItem in scale.items"
            class="first:[&>div]:rounded-l-large last:[&>div]:rounded-r-large flex w-0 flex-[1_1_0] flex-col"
          >
            <div class="bg-surface-container-lowest rounded-small h-28 w-full"></div>
            <span
              class="text-body-small text-on-surface-variant wrap-break-words w-full pt-1 text-center break-normal hyphens-auto"
            >
              {{ scaleItem.label }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <BaseStateLayer class="z-0" />
  </div>
</template>
<script lang="ts" setup>
  import { computed } from 'vue'
  import { useMembersStore } from '@/stores/members.store'
  import BaseStateLayer from '../../../../core/components/base/state-layer/BaseStateLayer.vue'
  import type { Scale } from '../../../../core/models/scale'
  import { type QuestionLabels } from '@/core/composables/use-question-analysis-new'
  import type { MemberOrUser } from '@/core/models/member/member-or-user'
  import type { ScoredPerspectiveAnalysis } from '@/core/models/surveys-analytics'
  import { SELF_PERSPECTIVE_KEY, type Perspective } from '@/core/models/perspective'
  import { toValueBarPerspectives } from '@/core/models/perspective-analysis'
  import { resolvePerspectiveLabel } from '@/core/models/perspective-label'
  import { useI18n } from 'vue-i18n'
  import DistributeItems from '../skill-container/DistributeItems.vue'
  import PerspectiveValueBarNew from './PerspectiveValueBarNew.vue'
  import PerspectiveShape from './PerspectiveShape.vue'
  import BaseMemberAvatarShaped from '../../base/avatar/BaseMemberAvatarShaped.vue'

  interface Props {
    expanded?: boolean
    content: string
    perspectives: ReadonlyArray<ScoredPerspectiveAnalysis>
    topicPerspectives: ReadonlyArray<Perspective>
    scale: Scale
    labels?: QuestionLabels[]
    subject: MemberOrUser
    index: number
  }

  const props = defineProps<Props>()

  const memberStore = useMembersStore()
  const { t } = useI18n()

  const perspectiveBarEntries = computed(() =>
    toValueBarPerspectives(props.perspectives, props.topicPerspectives),
  )

  const perspectiveLabel = (key: string) =>
    resolvePerspectiveLabel(key, props.topicPerspectives, {
      selfLabel: t('perspectives.self'),
      count: 1,
    })

  const distributedItems = computed(() => {
    const items: {
      value: number
      groupKey: string
      item: { id: string; member: MemberOrUser | null; perspectiveKey: string; label?: string }
    }[] = []

    for (const entry of props.perspectives) {
      const key = entry.key ?? entry.perspective?.key
      if (entry.average === null) {
        continue
      }

      if (key === SELF_PERSPECTIVE_KEY) {
        items.push({
          value: entry.average,
          groupKey: SELF_PERSPECTIVE_KEY,
          item: {
            id: SELF_PERSPECTIVE_KEY,
            member: props.subject,
            perspectiveKey: SELF_PERSPECTIVE_KEY,
          },
        })
        continue
      }

      for (const [index, feedback] of entry.feedbacks.entries()) {
        if (!key || feedback.value === null) continue
        items.push({
          value: feedback.value,
          groupKey: key,
          item: {
            id: `${key}-${feedback.senderId ?? index}`,
            member: feedback.senderId ? memberStore.getMemberById(feedback.senderId) : null,
            perspectiveKey: key,
            label: perspectiveLabel(key),
          },
        })
      }
    }

    return items
  })
</script>
